import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, apiGet, apiPut } from "../api/client";
import type { AuditScopeClassification, AuditScopeRecord, AuditScopesResponse } from "../api/types";
import { saveResultMessage } from "../lib/applyResult";
import { ScopeRepairPanel } from "./ScopeRepairPanel";

type ScopeRow = AuditScopeRecord;

const SCOPES_API = "/api/config/scopes";
const REFRESH_MS = 60_000;

function decoderKey(name: string): string {
  return name.startsWith("#") ? name.slice(1) : name;
}

function cleanRows(rows: ScopeRow[]): ScopeRow[] {
  // The wildcard is a structural decoder token, never a named scope.
  return rows.filter((row) => row.name.trim() !== "*").map((row) => ({
    ...row,
    configured: row.configured ?? row.sources.includes("configured"),
  }));
}

function sourceLabel(source: string): string | null {
  switch (source) {
    case "declared": return "Roster declaration";
    case "decoded": return "Decoded traffic";
    case "region": return "Region candidate";
    case "configured": return "Known candidate";
    case "regional-setting": return "Regional setting";
    default: return null;
  }
}

function mergeRefresh(draft: ScopeRow[], refreshed: ScopeRow[], editedNames: Set<string>): ScopeRow[] {
  const localByName = new Map(draft.map((row) => [row.name, row]));
  const merged = refreshed.map((row) => {
    const local = localByName.get(row.name);
    if (!local || !editedNames.has(row.name)) return row;
    return { ...row, classification: local.classification, configured: local.configured };
  });
  const refreshedNames = new Set(refreshed.map((row) => row.name));
  for (const row of draft) {
    if (!refreshedNames.has(row.name) && editedNames.has(row.name)) merged.push(row);
  }
  return merged;
}

export function ScopeListEditor() {
  const [serverScopes, setServerScopes] = useState<ScopeRow[]>([]);
  const [draftScopes, setDraftScopes] = useState<ScopeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const [saveMessage, setSaveMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [newName, setNewName] = useState("");
  const [newClassification, setNewClassification] = useState<AuditScopeClassification | "">("");
  const [addError, setAddError] = useState<string | null>(null);
  const editedNamesRef = useRef(new Set<string>());
  // Colour edits not saved yet: scope name -> "#rrggbb", or "" for back to the automatic colour.
  const [colourEdits, setColourEdits] = useState<Record<string, string>>({});

  const dirty = useMemo(() => {
    if (Object.keys(colourEdits).length > 0) return true;
    if (draftScopes.length !== serverScopes.length) return true;
    const current = new Map(serverScopes.map((row) => [row.name, row]));
    return draftScopes.some((row) => {
      const original = current.get(row.name);
      return !original || original.classification !== row.classification || original.configured !== row.configured;
    });
  }, [draftScopes, serverScopes, colourEdits]);
  const refresh = useCallback(async (showLoading = false, replaceDraft = false) => {
    if (savingRef.current) return;
    const generation = ++requestGenerationRef.current;
    if (showLoading) setLoading(true);
    try {
      const response = await apiGet<AuditScopesResponse>(SCOPES_API);
      if (generation !== requestGenerationRef.current || savingRef.current) return;
      const rows = cleanRows(response.scopes ?? []);
      setServerScopes(rows);
      setTruncated(response.truncated === true);
      setDraftScopes((previous) => {
        if (replaceDraft) {
          editedNamesRef.current.clear();
          return rows;
        }
        const localByName = new Map(previous.map((row) => [row.name, row]));
        const refreshedByName = new Map(rows.map((row) => [row.name, row]));
        for (const name of editedNamesRef.current) {
          const local = localByName.get(name);
          const refreshed = refreshedByName.get(name);
          if (local && refreshed && local.classification === refreshed.classification && local.configured === refreshed.configured) {
            editedNamesRef.current.delete(name);
          }
        }
        return mergeRefresh(previous, rows, editedNamesRef.current);
      });
      setLoadError(null);
    } catch (error) {
      if (generation !== requestGenerationRef.current || savingRef.current) return;
      setLoadError(error instanceof Error ? error.message : "Couldn't load recognized scopes.");
    } finally {
      if (generation === requestGenerationRef.current && !savingRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(true);
    const timer = window.setInterval(() => void refresh(false), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  function changeClassification(name: string, classification: AuditScopeClassification) {
    const current = draftScopes.find((row) => row.name === name);
    const original = serverScopes.find((row) => row.name === name);
    if (original && original.classification === classification && original.configured === current?.configured) {
      editedNamesRef.current.delete(name);
    } else {
      editedNamesRef.current.add(name);
    }
    setDraftScopes((rows) => rows.map((row) => row.name === name ? { ...row, classification } : row));
    setSaveMessage(null);
  }

  function changeColour(name: string, colour: string) {
    setColourEdits((edits) => ({ ...edits, [name]: colour }));
    setSaveMessage(null);
  }

  function addScope(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) {
      setAddError("Enter a scope name.");
      return;
    }
    if (!newClassification) {
      setAddError("Choose Regional or Local for this scope.");
      return;
    }
    if (name === "*") {
      setAddError("The wildcard is reserved and cannot be added as a scope.");
      return;
    }
    if (/[,\x00-\x1f\x7f]/.test(name)) {
      setAddError("Scope names can't contain commas or control characters.");
      return;
    }
    const existing = draftScopes.find((row) => row.name === name);
    if (existing) {
      if (existing.configured) {
        setAddError("That scope is already listed.");
        return;
      }
      editedNamesRef.current.add(name);
      setDraftScopes((rows) => rows.map((row) => row.name === name
        ? { ...row, classification: newClassification, configured: true }
        : row));
      setNewName("");
      setNewClassification("");
      setAddError(null);
      setSaveMessage(null);
      return;
    }
    if (draftScopes.some((row) => decoderKey(row.name) === decoderKey(name))) {
      setAddError("That spelling aliases a scope already in the list. Names with or without # refer to the same decoder scope.");
      return;
    }
    editedNamesRef.current.add(name);
    setDraftScopes((rows) => [...rows, { name, observed: false, sources: [], classification: newClassification, configured: true }]);
    setNewName("");
    setNewClassification("");
    setAddError(null);
    setSaveMessage(null);
  }

  async function save() {
    if (!dirty || saving) return;
    savingRef.current = true;
    requestGenerationRef.current += 1;
    setSaving(true);
    setSaveMessage(null);
    try {
      // Submit only changed/new records so server defaults and unrelated
      // legacy umbrella tokens stay untouched.
      const byName = new Map(serverScopes.map((row) => [row.name, row]));
      const changed = cleanRows(draftScopes).filter((row) => {
        if (!editedNamesRef.current.has(row.name)) return false;
        const original = byName.get(row.name);
        return !original || original.classification !== row.classification || original.configured !== row.configured;
      });
      const edits = colourEdits;
      const changes: Array<{ name: string; classification?: AuditScopeClassification; colour?: string }> = changed.map(
        ({ name, classification }) => ({ name, classification, ...(name in edits ? { colour: edits[name] } : {}) }),
      );
      for (const [name, colour] of Object.entries(edits)) {
        if (!changes.some((c) => c.name === name)) changes.push({ name, colour });
      }
      const response = await apiPut<AuditScopesResponse>(SCOPES_API, { changes });
      const rows = cleanRows(response.scopes ?? []);
      setServerScopes(rows);
      setDraftScopes(rows);
      setTruncated(response.truncated === true);
      editedNamesRef.current.clear();
      setColourEdits({});
      setLoadError(null);
      setSaveMessage(saveResultMessage(response.applied));
    } catch (error) {
      const message = error instanceof ApiError && error.status === 403
        ? "Only an admin can save scope settings. Your changes are still here."
        : error instanceof Error ? error.message : "Couldn't save scopes. Your changes are still here.";
      setSaveMessage({ kind: "error", text: message });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <section className="settings-pane scope-list-editor" aria-labelledby="scope-list-title">
      <div>
        <h3 id="scope-list-title">Scopes</h3>
        <p className="settings-pane-description">Known scope names and scopes declared or decoded from received data. Regional scopes cover broad areas; sharing only regional scopes does not put repeaters in the same local area.</p>
      </div>

      {loading && <div className="scope-list-loading" aria-live="polite">Loading recognized scopes…</div>}
      {truncated && <p className="scope-list-truncated">The recognized-scope list reached its display limit; some names may not appear.</p>}
      {!loading && loadError && (
        <div className="scope-list-error" role="alert">
          <span>Couldn't load recognized scopes: {loadError}</span>
          <button type="button" className="scope-list-link-button" onClick={() => void refresh(true)}>Retry</button>
        </div>
      )}
      {!loading && !loadError && draftScopes.length === 0 && <p className="scope-list-empty">No recognized scopes yet.</p>}

      {draftScopes.length > 0 && (
        <ul className="scope-list-rows" aria-label="Recognized scopes">
          {draftScopes.map((row) => (
            <li className="scope-list-row" key={row.name}>
              <div className="scope-list-name-block">
                <span className="scope-list-name">{row.name}</span>
                <span className="scope-list-meta" title={row.sources.map(sourceLabel).filter((label): label is string => label !== null).join(", ") || undefined}>
                  <span className="scope-list-source" data-kind={row.observed ? "observed" : row.configured ? "known" : "candidate"}>
                    {row.observed
                      ? "Observed"
                      : editedNamesRef.current.has(row.name) && row.configured && !serverScopes.find((scope) => scope.name === row.name)?.configured
                        ? "Unsaved name"
                        : row.configured
                          ? "Known candidate"
                          : row.sources.includes("region")
                            ? "Region candidate"
                            : row.sources.includes("regional-setting")
                              ? "Regional setting"
                              : "Recognized"}
                  </span>
                {row.alias_warning && <span className="scope-list-alias-warning">{row.alias_warning}</span>}
                </span>
              </div>
              <div className="scope-list-colour">
                <input
                  type="color"
                  aria-label={`Map colour for ${row.name}`}
                  value={colourEdits[row.name] || row.colour || "#8a8f98"}
                  disabled={saving}
                  onChange={(event) => changeColour(row.name, event.target.value)}
                />
                {row.colour_other && !(row.name in colourEdits ? colourEdits[row.name] !== "" : row.colour_custom) && (
                  <span className="scope-list-meta" title="Only the 15 scopes with the most repeaters get an automatic colour. Pick a colour to colour this one.">Other on the map</span>
                )}
                {(row.name in colourEdits ? colourEdits[row.name] !== "" : row.colour_custom) && (
                  <button type="button" className="scope-list-link-button" disabled={saving} onClick={() => changeColour(row.name, "")}>Automatic</button>
                )}
              </div>
              <div className="scope-list-choice" role="group" aria-label={`Classify ${row.name}`}>
                <button type="button" aria-pressed={row.classification === "regional"} disabled={saving} onClick={() => changeClassification(row.name, "regional")}>Regional</button>
                <button type="button" aria-pressed={row.classification === "local"} disabled={saving} onClick={() => changeClassification(row.name, "local")}>Local</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <form className="scope-list-add-form" onSubmit={addScope}>
        <label htmlFor="scope-list-new-name">Add an unseen scope</label>
        <p className="scope-list-add-help">Saved names help recognize scoped traffic. Names are case-sensitive.</p>
        <div className="scope-list-add-controls">
          <input id="scope-list-new-name" type="text" value={newName} maxLength={128} onChange={(event) => { setNewName(event.target.value); setAddError(null); }} placeholder="#scope" disabled={saving} />
          <select aria-label="Classification for new scope" value={newClassification} onChange={(event) => { setNewClassification(event.target.value as AuditScopeClassification | ""); setAddError(null); }} disabled={saving}>
            <option value="">Choose type</option>
            <option value="regional">Regional</option>
            <option value="local">Local</option>
          </select>
          <button type="submit" className="settings-save-btn" disabled={saving}>Add scope</button>
        </div>
        {addError && <p className="scope-list-error-text" role="alert">{addError}</p>}
      </form>

      <ScopeRepairPanel />

      <div className="settings-pane-actions">
        {saveMessage && <span className="settings-save-status" data-kind={saveMessage.kind} role="status">{saveMessage.text}</span>}
        <button type="button" className="scope-list-reset" onClick={() => { editedNamesRef.current.clear(); setColourEdits({}); setDraftScopes(serverScopes); setNewName(""); setNewClassification(""); setAddError(null); setSaveMessage(null); }} disabled={!dirty || saving}>Reset</button>
        <button type="button" className="settings-save-btn" onClick={() => void save()} disabled={!dirty || saving || loading || !!loadError}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}
